/**
 * Landing Page Component
 *
 * Public landing page for Saqr Skills platform.
 * Supports English and Arabic with RTL layout.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SignInModal } from '../components/SignInModal';
import { LanguageToggle } from '../components/LanguageToggle';
import { SEO } from '../components/SEO';
import { useAccess } from '../lib/auth-context';
import { useLocalizedPath } from '../lib/language-context';
import {
  getOrganizationSchema,
  getSoftwareApplicationSchema,
  getBreadcrumbSchema,
} from '../lib/structured-data';
import './LandingPage.css';

export function LandingPage() {
  const [showSignIn, setShowSignIn] = useState(false);
  const { isConfigured } = useAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation(['landing', 'common', 'seo']);
  const localizedPath = useLocalizedPath();

  const isArabic = i18n.language === 'ar';
  const currentPath = location.pathname === '/' || location.pathname === '/ar' ? (isArabic ? '/ar' : '') : location.pathname;

  // Structured data for home page
  const structuredData = [
    getOrganizationSchema(),
    getSoftwareApplicationSchema(),
    getBreadcrumbSchema([{ name: t('seo:home.title'), path: currentPath }]),
  ];

  // Redirect configured users to console
  useEffect(() => {
    if (isConfigured) {
      navigate('/console');
    }
  }, [isConfigured, navigate]);

  // IntersectionObserver for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page antialiased light">
      <SEO
        title={t('seo:home.title')}
        description={t('seo:home.description')}
        path={currentPath}
        structuredData={structuredData}
      />
      <div className="relative min-h-screen overflow-x-hidden pt-32 bg-white text-slate-900 selection:bg-sky-500/20 selection:text-sky-700">
        {/* Background Elements */}
        <div className="fixed inset-0 pointer-events-none z-0">
          {/* Vertical Lines */}
          <div className="absolute inset-0 mx-auto max-w-7xl grid-lines border-r border-l border-slate-100"></div>
          {/* Top Glow */}
          <div className="absolute top-0 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 w-[80vw] h-[400px] bg-[radial-gradient(ellipse_at_center,_var(--accent-glow),_transparent_70%)] opacity-40 blur-3xl"></div>
        </div>

        {/* Navigation */}
        <div className="fixed top-6 start-0 end-0 z-50 flex justify-center px-6 animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0s_both]">
          <nav className="flex w-full max-w-5xl items-center justify-between rounded-full border border-slate-200 bg-white/80 p-2 ps-6 shadow-xl backdrop-blur-xl ring-1 ring-slate-900/5">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-white">
                <iconify-icon icon="lucide:activity" width="18" height="18"></iconify-icon>
              </div>
              <span className="text-base font-bold tracking-tight text-slate-900">
                Saqr Skills
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
              <a href="#" className="no-underline text-slate-600 hover:text-sky-600 transition-colors">{t('common:nav.platform')}</a>
              <a href="#" className="no-underline text-slate-600 hover:text-sky-600 transition-colors">
                {t('common:nav.howItWorks')}
              </a>
              <a href="#" className="no-underline text-slate-600 hover:text-sky-600 transition-colors">
                {t('common:nav.enterprise')}
              </a>
              <a href="#" className="no-underline text-slate-600 hover:text-sky-600 transition-colors">{t('common:nav.resources')}</a>
            </div>
            <div className="flex items-center gap-3">
              <LanguageToggle />
              <button
                onClick={() => setShowSignIn(true)}
                className="hidden text-sm font-medium text-slate-600 hover:text-sky-600 sm:block px-2 cursor-pointer border-0 bg-transparent"
              >
                {t('common:nav.signIn')}
              </button>
              <a href={localizedPath('/book-call')} className="group relative flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-medium text-white transition-all hover:bg-slate-800 shadow-lg shadow-slate-900/20 no-underline">
                <span>{t('common:nav.requestWalkthrough')}</span>
                <iconify-icon icon="lucide:arrow-right" width="14" class="opacity-50 transition-transform group-hover:translate-x-0.5"></iconify-icon>
              </a>
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <main className="max-w-7xl z-10 mx-auto pt-12 px-6 pb-32 relative">
          {/* Hero Section */}
          <div className="mx-auto mb-24 max-w-4xl text-center">
            <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.1s_both] mb-8 inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-sky-700 shadow-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-500"></span>
              </span>
              {t('hero.badge')}
            </div>
            <h1 className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.2s_both] mb-6 text-6xl font-semibold leading-[0.95] tracking-tight text-slate-900 md:text-8xl">
              {t('hero.title')}
              <br />
              <span className="font-serif-accent italic text-slate-500 text-5xl md:text-7xl block mt-4">
                {t('hero.subtitle')}
              </span>
            </h1>
            <p className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.3s_both] mx-auto mb-10 max-w-xl text-xl font-normal leading-relaxed text-slate-600">
              {t('hero.description')}
            </p>
            <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.4s_both] flex flex-col items-center justify-center gap-6">
              <a
                href={localizedPath('/book-call')}
                className="h-12 rounded-full bg-sky-500 px-8 text-sm font-semibold text-white hover:bg-sky-600 transition-all shadow-lg shadow-sky-500/30 flex items-center gap-2 no-underline">
                {t('common:cta.talkToOurTeam')}
                <iconify-icon icon="lucide:arrow-right" width="16"></iconify-icon>
              </a>
              <div className="flex items-center gap-2 text-xs text-slate-500 font-medium bg-slate-50 px-3 py-1.5 rounded-full border border-slate-100">
                <iconify-icon icon="lucide:check-circle" class="text-sky-500"></iconify-icon>
                <span>{t('hero.integration')}</span>
              </div>
            </div>
          </div>
          <div className="mx-auto mt-24 mb-24 max-w-4xl text-center animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.5s_both]">
            <h2 className="text-4xl font-semibold text-slate-900 mb-6 tracking-tight">
              {t('blankPage.title')}
              <span className="font-serif-accent text-slate-500"> {t('blankPage.titleAccent')}</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              {t('blankPage.description')}
            </p>
          </div>

          {/* Dashboard Grid */}
          <div className="relative grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
            {/* Grid Decoration */}
            <div className="absolute -start-12 top-0 hidden text-[10px] font-mono text-slate-300 xl:block">
              01
            </div>
            <div className="absolute -end-12 top-0 hidden text-[10px] font-mono text-slate-300 xl:block">
              02
            </div>
            {/* Left: AI Authoring */}
            <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.5s_both] flex flex-col gap-6 md:col-span-4">
              <div className="relative h-full overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 p-8 shadow-sm transition-colors hover:border-sky-200 group">
                <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-sm border border-slate-100 text-sky-500">
                  <iconify-icon icon="lucide:sparkles" width="24"></iconify-icon>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  {t('features.aiAuthoring.title')}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t('features.aiAuthoring.description')}
                </p>
              </div>
            </div>
            {/* Middle: Live Voice Assessment */}
            <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.6s_both] relative md:col-span-4">
              <div className="flex flex-col h-full overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-xl transition-colors hover:border-sky-200">
                <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-slate-50 shadow-inner text-sky-500">
                  <iconify-icon icon="lucide:mic" width="24"></iconify-icon>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  {t('features.liveVoice.title')}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t('features.liveVoice.description')}
                </p>
                <div className="mt-8 flex items-end gap-1 h-12 opacity-50">
                  <div className="w-1 bg-sky-500 rounded-t-sm animate-pulse" style={{ height: '40%' }}></div>
                  <div className="w-1 bg-sky-400 rounded-t-sm animate-pulse" style={{ height: '70%', animationDelay: '0.1s' }}></div>
                  <div className="w-1 bg-sky-500 rounded-t-sm animate-pulse" style={{ height: '50%', animationDelay: '0.2s' }}></div>
                  <div className="w-1 bg-sky-600 rounded-t-sm animate-pulse" style={{ height: '90%', animationDelay: '0.3s' }}></div>
                  <div className="w-1 bg-sky-400 rounded-t-sm animate-pulse" style={{ height: '60%', animationDelay: '0.1s' }}></div>
                  <div className="w-1 bg-sky-500 rounded-t-sm animate-pulse" style={{ height: '80%', animationDelay: '0.4s' }}></div>
                  <div className="w-1 bg-sky-300 rounded-t-sm animate-pulse" style={{ height: '45%', animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
            {/* Right: Defensible Scoring */}
            <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.7s_both] relative md:col-span-4">
              <div className="group relative h-full overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 p-8 hover:border-sky-200 transition-colors">
                <div className="relative z-10 flex flex-col items-start">
                  <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-sm border border-slate-100 text-sky-500">
                    <iconify-icon icon="lucide:scale" width="24"></iconify-icon>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">
                    {t('features.defensibleScoring.title')}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {t('features.defensibleScoring.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Trusted By */}
        <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.8s_both] border-y border-slate-100 bg-white">
          <div className="mx-auto max-w-7xl px-6 py-10 text-center">
            <p className="text-sm font-semibold uppercase tracking-widest text-slate-400">
              {t('trustedBy')}
            </p>
          </div>
        </div>

        {/* Feature Section */}
        <section className="relative overflow-hidden bg-slate-50 py-32">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(240,249,255,1),_rgba(255,255,255,0))]"></div>

          <div className="relative z-10 mx-auto max-w-7xl px-6">
            <div className="mb-24 grid grid-cols-1 gap-12 md:grid-cols-2">
              <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.2s_both]">
                <h2 className="text-4xl font-semibold tracking-tight text-slate-900 md:text-5xl">
                  {t('missingLayer.title')}
                  <br />
                  <span className="font-serif-accent italic text-slate-500">
                    {t('missingLayer.titleAccent')}
                  </span>
                </h2>
              </div>
              <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.3s_both]">
                <p className="text-lg font-normal leading-relaxed text-slate-600">
                  {t('missingLayer.description')}
                </p>
                <div className="mt-8 flex gap-8">
                  <div className="flex flex-col gap-1">
                    <span className="text-3xl font-bold text-sky-600">{t('missingLayer.stat1')}</span>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      {t('missingLayer.stat1Label')}
                    </span>
                  </div>
                  <div className="h-auto w-px bg-slate-200"></div>
                  <div className="flex flex-col gap-1">
                    <span className="text-3xl font-bold text-sky-600">{t('missingLayer.stat2')}</span>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      {t('missingLayer.stat2Label')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature Cards */}
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {/* Card 1 */}
              <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.4s_both] group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 transition-colors hover:border-sky-200 hover:shadow-lg hover:shadow-sky-100/50">
                <div className="mb-6 inline-flex rounded-lg bg-sky-50 p-3 text-sky-600">
                  <iconify-icon icon="lucide:message-square" width="24"></iconify-icon>
                </div>
                <h3 className="mb-3 text-xl font-semibold text-slate-900">
                  {t('featureCards.voiceScenarios.title')}
                </h3>
                <p className="text-sm leading-relaxed text-slate-600">
                  {t('featureCards.voiceScenarios.description')}
                </p>
              </div>
              {/* Card 2 */}
              <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.5s_both] group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 transition-colors hover:border-sky-200 hover:shadow-lg hover:shadow-sky-100/50">
                <div className="mb-6 inline-flex rounded-lg bg-sky-50 p-3 text-sky-600">
                  <iconify-icon icon="lucide:presentation" width="24"></iconify-icon>
                </div>
                <h3 className="mb-3 text-xl font-semibold text-slate-900">
                  {t('featureCards.executiveDebriefs.title')}
                </h3>
                <p className="text-sm leading-relaxed text-slate-600">
                  {t('featureCards.executiveDebriefs.description')}
                </p>
              </div>
              {/* Card 3 */}
              <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.6s_both] group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 transition-colors hover:border-sky-200 hover:shadow-lg hover:shadow-sky-100/50">
                <div className="mb-6 inline-flex rounded-lg bg-sky-50 p-3 text-sky-600">
                  <iconify-icon icon="lucide:file-check" width="24"></iconify-icon>
                </div>
                <h3 className="mb-3 text-xl font-semibold text-slate-900">
                  {t('featureCards.knowledgeChecks.title')}
                </h3>
                <p className="text-sm leading-relaxed text-slate-600">
                  {t('featureCards.knowledgeChecks.description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Materials to Measurement Section */}
        <section className="relative border-t border-slate-200 bg-white py-32 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(14,165,233,0.03),_transparent_60%)]"></div>
          <div className="relative z-10 mx-auto max-w-7xl px-6">
            <div className="mb-20 text-center">
              <h2 className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.2s_both] text-3xl font-semibold tracking-tight text-slate-900 md:text-5xl">
                {t('materialsToMeasurement.title')}
              </h2>
            </div>
            <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.4s_both] relative rounded-3xl border border-slate-200 bg-slate-50 p-8 md:p-12 shadow-xl">
              {/* Connector Line */}
              <div className="absolute top-[36%] md:top-1/2 start-0 w-full -translate-y-1/2 px-12 md:px-24 hidden md:block pointer-events-none z-0">
                <div className="relative h-px w-full bg-slate-200"></div>
              </div>
              <div className="grid grid-cols-1 gap-12 md:grid-cols-3 relative z-10">
                {/* Step 1 */}
                <div className="group flex flex-col items-center text-center">
                  <div className="relative mb-6 flex h-20 w-20 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-lg transition-all duration-500 group-hover:-translate-y-2 group-hover:border-sky-500/30">
                    <iconify-icon icon="lucide:upload-cloud" width="28" class="text-slate-400 transition-colors group-hover:text-sky-500"></iconify-icon>
                    <div className="absolute -end-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-sky-100 text-sky-600 font-bold text-xs shadow-sm">
                      1
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 tracking-tight">
                    {t('materialsToMeasurement.step1.title')}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600 max-w-[240px]">
                    {t('materialsToMeasurement.step1.description')}
                  </p>
                </div>
                {/* Step 2 */}
                <div className="group flex flex-col items-center text-center">
                  <div className="relative mb-6 flex h-20 w-20 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-lg transition-all duration-500 group-hover:-translate-y-2 group-hover:border-sky-500/30">
                    <iconify-icon icon="lucide:cpu" width="28" class="text-slate-400 transition-colors group-hover:text-sky-500"></iconify-icon>
                    <div className="absolute -end-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-sky-100 text-sky-600 font-bold text-xs shadow-sm">
                      2
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 tracking-tight">
                    {t('materialsToMeasurement.step2.title')}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600 max-w-[240px]">
                    {t('materialsToMeasurement.step2.description')}
                  </p>
                </div>
                {/* Step 3 */}
                <div className="group flex flex-col items-center text-center">
                  <div className="relative mb-6 flex h-20 w-20 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-lg transition-all duration-500 group-hover:-translate-y-2 group-hover:border-sky-500/30">
                    <iconify-icon icon="lucide:send" width="28" class="text-slate-400 transition-colors group-hover:text-sky-500"></iconify-icon>
                    <div className="absolute -end-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-sky-100 text-sky-600 font-bold text-xs shadow-sm">
                      3
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 tracking-tight">
                    {t('materialsToMeasurement.step3.title')}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600 max-w-[240px]">
                    {t('materialsToMeasurement.step3.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CLI/Code Section */}
        <section className="border-t border-slate-200 bg-slate-50 py-32">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
              <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.2s_both] order-2 lg:order-1 rtl:lg:order-2">
                <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl p-8">
                  <div className="flex flex-col gap-6">
                    <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="flex-shrink-0 text-sky-500">
                        <iconify-icon icon="lucide:check" width="20"></iconify-icon>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 text-sm">
                          {t('assessmentsComplete.point1Title')}
                        </h4>
                        <p className="text-xs text-slate-500 mt-1">
                          {t('assessmentsComplete.point1Desc')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="flex-shrink-0 text-sky-500">
                        <iconify-icon icon="lucide:check" width="20"></iconify-icon>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 text-sm">
                          {t('assessmentsComplete.point2Title')}
                        </h4>
                        <p className="text-xs text-slate-500 mt-1">
                          {t('assessmentsComplete.point2Desc')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="flex-shrink-0 text-sky-500">
                        <iconify-icon icon="lucide:check" width="20"></iconify-icon>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 text-sm">
                          {t('assessmentsComplete.point3Title')}
                        </h4>
                        <p className="text-xs text-slate-500 mt-1">
                          {t('assessmentsComplete.point3Desc')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.3s_both] order-1 lg:order-2 rtl:lg:order-1">
                <h2 className="mb-6 text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
                  {t('assessmentsComplete.title')}
                </h2>
                <p className="mb-8 text-slate-600 font-normal leading-relaxed">
                  {t('assessmentsComplete.description')}
                </p>
                <ul className="space-y-4 text-sm text-slate-600">
                  <li className="flex items-center gap-3">
                    <iconify-icon icon="lucide:check-circle-2" class="text-sky-500"></iconify-icon>
                    {t('assessmentsComplete.list1')}
                  </li>
                  <li className="flex items-center gap-3">
                    <iconify-icon icon="lucide:check-circle-2" class="text-sky-500"></iconify-icon>
                    {t('assessmentsComplete.list2')}
                  </li>
                  <li className="flex items-center gap-3">
                    <iconify-icon icon="lucide:check-circle-2" class="text-sky-500"></iconify-icon>
                    {t('assessmentsComplete.list3')}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="border-t border-slate-200 bg-white py-24">
          <div className="mx-auto max-w-7xl px-6">
            <div className="mx-auto max-w-3xl text-center animate-on-scroll [animation:fadeInUp_0.8s_ease-out_0.2s_both]">
              <span className="text-xs font-bold text-sky-600 uppercase tracking-widest mb-4 block">
                {t('pricing.badge')}
              </span>
              <h2 className="mb-6 text-3xl font-semibold text-slate-900">
                {t('pricing.title')}
              </h2>
              <p className="mb-10 text-lg text-slate-600 leading-relaxed">
                {t('pricing.description')}
              </p>
              <a href={localizedPath('/book-call')} className="inline-block rounded-full bg-slate-900 px-8 py-3 text-sm font-semibold text-white transition-all hover:bg-slate-800 shadow-xl shadow-slate-900/10 hover:shadow-slate-900/20 no-underline">
                {t('common:nav.requestWalkthrough')}
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-slate-200 bg-slate-50 pb-12 pt-24">
          <div className="mx-auto max-w-7xl px-6">
            <div className="mb-16 flex flex-col justify-between gap-12 md:flex-row">
              <div className="max-w-xs">
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex h-6 w-6 items-center justify-center rounded bg-slate-900 text-white">
                    <iconify-icon icon="lucide:activity" width="14" height="14"></iconify-icon>
                  </div>
                  <span className="text-lg font-bold tracking-tight text-slate-900">
                    Saqr Skills
                  </span>
                </div>
                <p className="text-sm leading-relaxed text-slate-500">
                  {t('common:footer.tagline')}
                </p>
              </div>
              <div className="flex gap-16 text-sm text-slate-500">
                <div className="flex flex-col gap-4">
                  <span className="font-bold text-slate-900">{t('common:footer.platform')}</span>
                  <a href={localizedPath('/terms')} className="no-underline text-slate-500 hover:text-sky-600">{t('common:footer.features')}</a>
                  <a href={localizedPath('/terms')} className="no-underline text-slate-500 hover:text-sky-600">{t('common:footer.howItWorks')}</a>
                  <a href={localizedPath('/terms')} className="no-underline text-slate-500 hover:text-sky-600">{t('common:footer.security')}</a>
                </div>
                <div className="flex flex-col gap-4">
                  <span className="font-bold text-slate-900">{t('common:footer.company')}</span>
                  <a href="#" className="no-underline text-slate-500 hover:text-sky-600">{t('common:footer.aboutSaqr')}</a>
                  <a href="https://saqracademy.com" target="_blank" rel="noopener noreferrer" className="no-underline text-slate-500 hover:text-sky-600">{t('common:footer.saqrAcademy')}</a>
                  <a href="https://saqrintelligence.com" target="_blank" rel="noopener noreferrer" className="no-underline text-slate-500 hover:text-sky-600">{t('common:footer.saqrIntelligence')}</a>
                </div>
                <div className="flex flex-col gap-4">
                  <span className="font-bold text-slate-900">{t('common:footer.connect')}</span>
                  <a href="https://www.linkedin.com/company/saqr-academy" target="_blank" rel="noopener noreferrer" className="no-underline text-slate-500 hover:text-sky-600">{t('common:footer.linkedin')}</a>
                  <a href="mailto:hello@saqracademy.com" className="no-underline text-slate-500 hover:text-sky-600">{t('common:footer.email')}</a>
                  <a href={localizedPath('/book-call')} className="no-underline text-slate-500 hover:text-sky-600">{t('common:footer.contact')}</a>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-slate-200 pt-8">
              <p className="text-xs text-slate-500">
                &copy; {t('common:footer.copyright')}
              </p>
              <div className="flex gap-4 items-center">
                <a href={localizedPath('/terms')} className="no-underline text-xs text-slate-500 hover:text-sky-600">
                  {t('common:footer.privacyPolicy')}
                </a>
                <a href={localizedPath('/terms')} className="no-underline text-xs text-slate-500 hover:text-sky-600">
                  {t('common:footer.termsOfService')}
                </a>
              </div>
            </div>
          </div>
        </footer>

        {/* Sign In Modal */}
        <SignInModal isOpen={showSignIn} onClose={() => setShowSignIn(false)} />
      </div>
    </div>
  );
}
